/* eslint-disable max-len */
import React, { useEffect, useState, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CPagination, CPaginationItem, CTooltip, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CRow, CCol, CFormInput, CCollapse,CBadge
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilTrash, cilFilter, cilX, cilSearch, cilArrowTop, cilArrowBottom } from '@coreui/icons';
import { mainColors } from '../../helpers/colors';
import {
  deleteSupplierRequest, getSupplierRequest
} from '../../store/supplier/supplierActions';
import './SupplierTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import ExportData from '../ExportData/ExportData';

function SupplierTable(props) {
  const { supplierList, isLoading, deleteSupplierRequest } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index', 'SupplierName', 'SupplierDescription', 'Address', 'Contact', 'CepTel', 'Telefon', 'Email', 'actions'], 'SupplierTable');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    SupplierName: '',
    SupplierDescription: '',
    Address: '',
    Contact: '',
    CepTel: '',
    Telefon: '',
    Email: ''
  });

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  useEffect(() => {
    dispatch(getSupplierRequest({}));
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [supplierList, filters]);

  const handleEdit = (item) => {
    navigate(`/suppliers/edit/${item.SupplierId}`, { state: { supplier: item } });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          SupplierId: deleteModal.item.SupplierId
        };
        deleteSupplierRequest(deleteData);
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      SupplierName: '',
      SupplierDescription: '',
      Address: '',
      Contact: '',
      CepTel: '',
      Telefon: '',
      Email: ''
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const descendingComparator = (a, b, orderByKey) => {
    if (b[orderByKey] === null || b[orderByKey] === undefined) return -1;
    if (a[orderByKey] === null || a[orderByKey] === undefined) return 1;
    if (b[orderByKey] < a[orderByKey]) return -1;
    if (b[orderByKey] > a[orderByKey]) return 1;
    return 0;
  };

  const getComparator = (ord, orderByKey) => {
    return ord === 'desc' ? (a, b) => descendingComparator(a, b, orderByKey) : (a, b) => -descendingComparator(a, b, orderByKey);
  };

  const stableSort = (array, comparator) => {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const ord = comparator(a[0], b[0]);
      if (ord !== 0) return ord;
      return a[1] - b[1];
    });
    return stabilized.map(el => el[0]);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
  };

  const columns = [
    { id: 'index', label: '#', sortable: false, filterable: false },
    { id: 'SupplierName', label: t('supplierName'), sortable: true, filterable: true },
    { id: 'SupplierDescription', label: t('supplierDescription'), sortable: true, filterable: true },
    { id: 'Address', label: t('address'), sortable: true, filterable: true },
    { id: 'Contact', label: t('contact'), sortable: true, filterable: true },
    { id: 'CepTel', label: t('mobilePhone'), sortable: true, filterable: true },
    { id: 'Telefon', label: t('userPhone'), sortable: true, filterable: true },
    { id: 'Email', label: t('email'), sortable: true, filterable: true },
    { id: 'actions', label: t('actions'), sortable: false, filterable: false }
  ];

  // Filtrelenmiş verileri hesapla
  const filteredData = useMemo(() => {
    return supplierList.filter(item => {
      return Object.keys(filters).every(key => {
        const filterValue = filters[key].toLowerCase();
        if (!filterValue) return true;

        const itemValue = item[key]?.toString().toLowerCase() || '';
        return itemValue.includes(filterValue);
      });
    });
  }, [supplierList, filters]);

  const getSortedData = () => {
    const sorted = orderBy ? stableSort(filteredData, getComparator(order, orderBy)) : filteredData;
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sorted.slice(startIndex, endIndex);
  };

  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <CPaginationItem key={i} active={i === page} onClick={() => handleChangePage(i)}>
        {i}
      </CPaginationItem>
    );
  }

  const activeFilterCount = Object.values(filters).filter(value => value.trim() !== '').length;

  const exportColumns = useMemo(() => {
    return columns.filter(col => !['actions', 'index'].includes(col.id));
  }, [columns]);

  const buildCellValue = (colId, item) => {
    return item[colId] ?? '';
  };

  return (
    <div className="supplier-table-container">
      <CCard>
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? "primary" : "outline-primary"}
                  variant={showFilters ? "solid" : "outline"}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                  {supplierList.length !== totalRows && (
                    <span className="filtered-info ms-1">
                      ({supplierList.length} {t('totalRecords')})
                    </span>
                  )}
                </span>
                <ExportData
                  columns={exportColumns}
                  rows={getSortedData()}
                  allRows={(orderBy ? stableSort(supplierList, getComparator(order, orderBy)) : supplierList)}
                  buildCellValue={buildCellValue}
                  filenamePrefix="suppliers_export"
                  triggerDisabled={!filteredData || filteredData.length === 0}
                />
              </div>
            </div>
          </div>

          {/* Filtre Alanları */}
          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3">
                {columns.filter(col => col.filterable).map((column) => (
                  <CCol key={column.id} xs={12} sm={6} md={4} lg={3}>
                    <div className="filter-field">
                      <label className="filter-label">{column.label}</label>
                      <div className="filter-input-wrapper">
                        <CIcon icon={cilSearch} className="filter-input-icon" />
                        <CFormInput
                          type="text"
                          placeholder={`${t('search')} ${column.label.toLowerCase()}...`}
                          value={filters[column.id]}
                          onChange={(e) => handleFilterChange(column.id, e.target.value)}
                          className="filter-input"
                          size="sm"
                        />
                        {filters[column.id] && (
                          <CButton
                            color="link"
                            size="sm"
                            className="filter-clear-btn"
                            onClick={() => handleFilterChange(column.id, '')}
                          >
                            <CIcon icon={cilX} size="sm" />
                          </CButton>
                        )}
                      </div>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell key={column.id} className="table-header-cell" style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }} onClick={() => column.sortable && handleRequestSort(column.id)}>
                      {column.label}
                      {column.sortable && orderBy === column.id && (
                        <CIcon icon={order === 'asc' ? cilArrowTop : cilArrowBottom} size="sm" className="ms-1" />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <CTableRow key={item.SupplierId || index}>
                    <CTableDataCell>{(page - 1) * rowsPerPage + index + 1}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="primary" shape="rounded-pill">
                        {item.SupplierName}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>{item.SupplierDescription}</CTableDataCell>
                    <CTableDataCell>{item.Address}</CTableDataCell>
                    <CTableDataCell>{item.Contact}</CTableDataCell>
                    <CTableDataCell>{item.CepTel}</CTableDataCell>
                    <CTableDataCell>{item.Telefon}</CTableDataCell>
                    <CTableDataCell>{item.Email}</CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('editSupplier')}>
                          <div className="cursor-pointer p-1" onClick={() => handleEdit(item)}>
                            <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                          </div>
                        </CTooltip>
                        <CTooltip content={t('deleteSupplier')}>
                          <div className="cursor-pointer p-1" onClick={() => handleDeleteClick(item)}>
                            <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center py-4">
                    <div className="no-data-message">
                      {isLoading ? (
                        <div className="loading-message">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          {t('loadingData')}
                        </div>
                      ) : activeFilterCount > 0 ? (
                        <div className="no-results-message">
                          <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                          <div>{t('noResultsFound')}</div>
                          <CButton
                            color="link"
                            size="sm"
                            onClick={clearFilters}
                            className="mt-2"
                          >
                            {t('clearFiltersToSeeAll')}
                          </CButton>
                        </div>
                      ) : (
                        <div className="text-muted">{t('noDataFound')}</div>
                      )}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select className="form-select form-select-sm" value={rowsPerPage} onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))} style={{ width: 'auto' }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              <CPagination className="mb-0">
                <CPaginationItem onClick={() => page > 1 && handleChangePage(page - 1)} disabled={page === 1}>&laquo;</CPaginationItem>
                {paginationItems}
                <CPaginationItem onClick={() => page < totalPages && handleChangePage(page + 1)} disabled={page === totalPages}>&raquo;</CPaginationItem>
              </CPagination>
              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteSupplier')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteSupplier')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('supplierName')}:</strong> {deleteModal.item.SupplierName}<br />
              <strong>{t('supplierDescription')}:</strong> {deleteModal.item.SupplierDescription}<br />
              <strong>{t('email')}:</strong> {deleteModal.item.Email || '-'}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('supplierDeleteWarning')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>{t('cancel')}</CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>{t('delete')}</CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  supplierList: state.supplierReducer.supplierList || [],
  isLoading: state.supplierReducer.isLoading,
});

const mapDispatchToProps = (dispatch) => ({
  deleteSupplierRequest: (payload) => dispatch(deleteSupplierRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierTable); 